import React, { useCallback } from 'react';
import { connect } from 'react-redux';
import {
  FaPen as EditIcon,
  FaTrash as TrashIcon,
} from 'react-icons/fa';

import { BaseContext } from '../../../shared/gridEdit/columnDefinitionsUtils';
import { isEditingMode, RowWithMetadata } from '../../../shared/gridEdit/useEditableGridLogic';
import ValidationMessagesCellRenderer from '../../../shared/gridEdit/validationMessagesCellRenderer.component';
import { YesNoFlag } from '../../../shared/types';
import type { ScenarioObligation } from '../../../shared/queries/scenarios';

import { showDeleteScenarioObligationModal } from './store/actions';
import { getScenarioObligationUniqueId } from './utils';

type ScenarioObligationAdminColumnRendererProps = {
    data: RowWithMetadata<ScenarioObligation>,
    rowIndex: number,
    showDeleteScenarioObligationModal: (arg: { entityToRemove: ScenarioObligation, rowIndex: number }) => void,
    context: BaseContext & { isScenarioLocked: boolean },
    editRow: (row: RowWithMetadata<ScenarioObligation>) => void,
}

const isScenarioObligationRowEdited = ({
  editedRowId,
  scenarioObligation,
}: {
  editedRowId?: string,
  scenarioObligation: RowWithMetadata<ScenarioObligation>
}) => getScenarioObligationUniqueId(scenarioObligation) === editedRowId;

const ScenarioObligationAdminColumnRenderer = (props: ScenarioObligationAdminColumnRendererProps) => {
  const {
    data: scenarioObligation,
    rowIndex,
    showDeleteScenarioObligationModal,
    context: {
      mode,
      editedRowId,
      isScenarioLocked,
    },
    editRow,
  } = props;

  const onEditClick = useCallback(() => {
    editRow(scenarioObligation);
  }, [editRow, scenarioObligation]);

  const onDeleteClick = useCallback(() => {
    showDeleteScenarioObligationModal({ entityToRemove: scenarioObligation, rowIndex });
  }, [showDeleteScenarioObligationModal, scenarioObligation, rowIndex]);

  const isEditingDisabled = isEditingMode(mode) || isScenarioLocked;
  const isRemovingDisabled = isEditingMode(mode) || scenarioObligation.added !== YesNoFlag.Y || isScenarioLocked;

  if (isEditingMode(mode) && isScenarioObligationRowEdited({ editedRowId, scenarioObligation })) {
    return <ValidationMessagesCellRenderer {...props}/>;
  }

  return (
    <div>
      <button
        className="cell-icon-button"
        title="Edit Scenario Obligation"
        onClick={onEditClick}
        disabled={isEditingDisabled}
      >
        <EditIcon size={16} className="cell-icon disabled-visible" />
      </button>
      <button
        className="cell-icon-button"
        title="Remove Scenario Obligation"
        onClick={onDeleteClick}
        disabled={isRemovingDisabled}
      >
        <TrashIcon size={16} className="cell-icon disabled-visible" />
      </button>
    </div>
  );
};

export default connect(null, {
  showDeleteScenarioObligationModal,
})(ScenarioObligationAdminColumnRenderer);
